import React from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { listParts } from './recognitionUtils'

/** Up to four photos, as the share card (`recognitionCard.service.js` MAX_NAMES); beyond four, "and N more". */
const MAX_SHOWN = 4

/*
 * The default arts are 3:1 banners with an off-white background, the motif on
 * the right and the left half left empty for the text (the designer's arts,
 * 2026-10-29). The text is dark, with no band, tint or shadow: #1C1917 and
 * #44403C on the off-white are about 16:1 and 9:1. The art is anchored right,
 * so a capped height (the Feed) only trims empty space on the left.
 * A custom upload may have no empty left side: the left of the art is
 * measured, and only when the dark text would fall under 4.5:1 there a soft
 * white veil goes behind it (the share card PNG does the same,
 * `recognitionCard.service.js` textOverArt).
 */
const TEXT = 'text-[#1C1917]'
const MUTED = 'text-[#44403C]'
/* Under the art (`sm`) the text sits on the card, not on the off-white: in the dark theme it follows the card (F-191). */
const BELOW_TEXT = 'dark:text-foreground'
const BELOW_MUTED = 'dark:text-foreground-secondary'
const CUSTOM_VEIL = 'bg-[linear-gradient(to_right,rgba(255,255,255,0.82)_0%,rgba(255,255,255,0.82)_42%,rgba(255,255,255,0)_64%)]'

/* WCAG relative luminance of an sRGB channel triple, and the contrast of the dark text (#1C1917) on it. */
const channel = (v: number) => {
  const c = v / 255
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}
const luminance = (r: number, g: number, b: number) => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
const DARK_TEXT_LUMINANCE = luminance(0x1c, 0x19, 0x17)

/**
 * Whether a custom art needs the white veil: the 5th percentile of the
 * luminance on the art's left half, against the dark text. While unknown, or
 * when the image cannot be read (no CORS), the veil stays (the safe side).
 */
const useNeedsVeil = (artUrl: string | null | undefined, enabled: boolean): boolean => {
  const [needs, setNeeds] = React.useState(true)
  React.useEffect(() => {
    if (!enabled || !artUrl) return undefined
    let alive = true
    setNeeds(true)
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = 150
        canvas.height = 100
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        /* The left half of the art as shown (3:1, anchored right like the img). */
        const shownW = img.naturalHeight * 3
        const left = Math.max(0, img.naturalWidth - shownW)
        ctx.drawImage(img, left, 0, Math.min(shownW, img.naturalWidth) / 2, img.naturalHeight, 0, 0, 150, 100)
        const { data } = ctx.getImageData(0, 0, 150, 100)
        const values: number[] = []
        for (let i = 0; i < data.length; i += 4 * 3) values.push(luminance(data[i], data[i + 1], data[i + 2]))
        values.sort((a, b) => a - b)
        const dark = values[Math.floor(values.length * 0.05)] ?? 0
        if (alive) setNeeds((dark + 0.05) / (DARK_TEXT_LUMINANCE + 0.05) < 4.5)
      } catch {
        /* A tainted canvas (no CORS): keep the veil. */
      }
    }
    /* A distinct URL so the browser does not reuse a non-CORS copy of the same image. */
    img.src = artUrl.startsWith('blob:') || artUrl.startsWith('data:') ? artUrl : `${artUrl}${artUrl.includes('?') ? '&' : '?'}cors=1`
    return () => { alive = false }
  }, [artUrl, enabled])
  return enabled && needs
}

interface Person {
  id: string
  name: string | null
  avatar?: string | null
}

interface Props {
  artUrl?: string | null
  /** The kind's phrase: the text's first line and the image's alt text. */
  phrase: string
  people: Person[]
  /** Who recognized: a small badge on the last recipient's photo. */
  sender?: Person | null
  /**
   * `thumb`: the "Reconhecer" grid and the settings list, art only.
   * `feed`: the card inside the Feed, height capped at 240 px.
   */
  size?: 'card' | 'thumb' | 'feed'
  /** The community's own art (`hasCustomArt`; no empty left side guaranteed): measured, veiled when the dark text needs it. */
  custom?: boolean
  className?: string
}

/**
 * F-185: a recognition's art at 3:1 with the kind's phrase, the photos
 * (overlapping up to four) and the names on its empty left side. On a
 * narrow card the art is too short for the text, so the same block goes
 * right under it, on the card's white. One component for the Feed, the wall, the
 * recognition page and the upload preview. The art comes resolved from the
 * API (`artUrl`); the names are HTML, never burned into the image.
 */
export const RecognitionArt = ({ artUrl, phrase, people, sender, size = 'card', custom, className }: Props) => {
  const { t, i18n } = useTranslation('recognition')
  const [failed, setFailed] = React.useState(false)
  React.useEffect(() => { setFailed(false) }, [artUrl])

  const shown = people.slice(0, MAX_SHOWN)
  const nameOf = (p: Person) => p.name || t('card.someone')
  /*
   * Never wrap inside a name: one recipient per line. Two: "Bruno Sato" / "e Diego Alves" (the connector
   * as the language's list format puts it). Three or more: the first / "Second e mais N".
   */
  const nameLines = ((): string[] => {
    const names = people.map(nameOf)
    if (names.length <= 1) return names
    if (names.length > 2) return [names[0], t('art.secondAndMore', { name: names[1], count: names.length - 2 })]
    const connector = listParts(names, i18n.language).find((part) => part.type === 'literal')?.value.trim() || ''
    return [names[0], `${connector} ${names[1]}`.trim()]
  })()
  const veil = useNeedsVeil(artUrl, Boolean(custom) && size !== 'thumb')

  /*
   * Where the text goes depends on the card's own width, not the screen's: the
   * wall's two columns make narrow cards on a wide screen. Below 400 px the
   * art's left half cannot hold it, so it goes under the art.
   */
  const boxRef = React.useRef<HTMLDivElement>(null)
  const [width, setWidth] = React.useState(() => (typeof window !== 'undefined' && window.innerWidth >= 640 ? 600 : 0))
  React.useEffect(() => {
    const el = boxRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => setWidth(entries[0]?.contentRect.width ?? 0))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const layout: 'below' | 'md' | 'lg' = width < 400 ? 'below' : width < 760 ? 'md' : 'lg'

  const text = (scale: 'lg' | 'md' | 'sm') => {
    return (
      /* A fixed height (phrase + two name lines) with the content at its top: the phrase sits at the same height
         in every card, and the block, centered on the art, stays centered whatever the names. */
      <div className={cn('flex min-w-0 items-start gap-3', scale === 'lg' ? 'h-[70px]' : scale === 'md' ? 'h-[56px]' : undefined)}>
        {shown.length > 0 && (
          <div className={cn('flex flex-shrink-0 -space-x-2.5', sender && 'pb-1 pr-1.5', scale !== 'sm' && 'mt-0.5')}>
            {shown.map((p, i) => {
              const avatar = (
                <Avatar key={p.id} src={p.avatar} alt={p.name || ''} fallback={p.name || ''} size="sm"
                  className={cn('ring-2 ring-white', scale === 'lg' ? 'h-12 w-12' : scale === 'md' ? 'h-10 w-10' : 'h-9 w-9')} />
              )
              if (!sender || i !== shown.length - 1) return avatar
              /* Who recognized, as a "from" badge on the last recipient's photo. */
              const from = t('art.fromBadge', { name: sender.name || t('card.someone') })
              return (
                <span key={p.id} className="relative inline-flex">
                  {avatar}
                  <span className="absolute -bottom-1 -right-1.5 rounded-full" title={from} data-testid="recognition-art-sender">
                    <Avatar src={sender.avatar} alt={from} fallback={sender.name || ''} size="sm"
                      className={cn('ring-2 ring-white', scale === 'lg' ? 'h-6 w-6' : 'h-5 w-5')} />
                  </span>
                </span>
              )
            })}
          </div>
        )}
        <div className="min-w-0 flex-1">
          {/* The phrase is never cut: it wraps, a touch smaller and tighter on a narrow card. */}
          <p className={cn('break-words font-semibold uppercase leading-snug', MUTED, scale === 'sm' && BELOW_MUTED, scale === 'lg' ? 'text-xs tracking-wide' : 'text-[11px] tracking-normal')}
            data-testid="recognition-art-phrase">{phrase}</p>
          <div className={cn('font-bold leading-tight', TEXT, scale === 'sm' && BELOW_TEXT, scale === 'lg' ? 'text-xl' : 'text-base')} data-testid="recognition-art-names">
            {nameLines.map((line, i) => <p key={i} className="truncate whitespace-nowrap" title={line}>{line}</p>)}
          </div>
        </div>
      </div>
    )
  }

  const art = (
    <div className={cn('theme-light relative w-full overflow-hidden bg-white',
      size === 'thumb' ? 'aspect-[2/1]' : 'aspect-[3/1]', size === 'feed' && 'max-h-60', size === 'thumb' && className)}
      data-testid="recognition-art">
      {artUrl && !failed && (
        <img src={artUrl} alt={phrase} loading="lazy" decoding="async" onError={() => setFailed(true)}
          className="absolute inset-0 h-full w-full object-cover object-right" />
      )}
      {size !== 'thumb' && layout !== 'below' && veil && <div className={cn('pointer-events-none absolute inset-0', CUSTOM_VEIL)} aria-hidden data-testid="recognition-art-veil" />}
      {size !== 'thumb' && layout !== 'below' && (
        /* Centered, but the block is always as tall as a phrase plus two name lines (one name leaves the second
           empty), so the phrase sits at the same height in every card of a row. */
        <div className={cn('absolute inset-y-0 left-0 flex min-w-0 items-center pr-2', layout === 'lg' ? 'w-1/2 pl-7' : 'w-[58%] pl-5')} data-testid="recognition-art-text">
          {text(layout)}
        </div>
      )}
    </div>
  )

  if (size === 'thumb') return art
  return (
    <div ref={boxRef} className={cn('min-w-0', className)}>
      {art}
      {/* A narrow card (a phone, a wall column) has a ~115-150 px art: the phrase and the names go right under it. */}
      {layout === 'below' && <div className="px-5 pt-4" data-testid="recognition-art-text-below">{text('sm')}</div>}
    </div>
  )
}
